import React, { useEffect } from 'react';
import SEO from './SEO';
import MyCleanStudioHeader from './MyCleanStudioHeader';
import MyCleanStudioFooter from './MyCleanStudioFooter';
import './LegalPages.css';

function TermsOfService() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO
        title="Terms of Service - MyCleanStudio"
        description="Read the terms of service for MyCleanStudio. Understand your rights and responsibilities when using our content compliance checker."
        keywords="terms of service, terms and conditions, MyCleanStudio terms"
        url="/terms"
      />
      <div className="legal-page">
        {/* Header */}
        <MyCleanStudioHeader />
      <div className="legal-container">

        {/* Content */}
        <main className="legal-content">
          <h1>Terms of Service</h1>
          <p className="legal-updated">Last updated: September 2026</p>

          <section>
            <h2>Agreement to Terms</h2>
            <p>
              By accessing or using MyCleanStudio ("Service"), you agree to be bound by these
              Terms of Service ("Terms"). If you do not agree to these Terms, please do not
              use our Service.
            </p>
          </section>

          <section>
            <h2>Description of Service</h2>
            <p>
              MyCleanStudio is a content moderation tool that helps content creators analyze
              their videos and transcripts for potential platform guideline violations before
              publishing. Our Service includes:
            </p>
            <ul>
              <li>Video content analysis for visual moderation</li>
              <li>Transcript analysis for content compliance</li>
              <li>Compliance scoring and recommendations</li>
              <li>Analysis history management</li>
            </ul>
          </section>

          <section>
            <h2>User Accounts</h2>
            <p>
              You may use some features of our Service without an account. To access full
              features including analysis history, you must create an account by signing
              in with Google.
            </p>
            <p>You are responsible for:</p>
            <ul>
              <li>Maintaining the security of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized access</li>
            </ul>
          </section>

          <section>
            <h2>Acceptable Use</h2>
            <p>You agree NOT to use our Service to:</p>
            <ul>
              <li>Upload content that you do not have rights to</li>
              <li>Upload content depicting illegal activities</li>
              <li>Attempt to circumvent our security measures</li>
              <li>Use the Service for any unlawful purpose</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Upload malware or malicious content</li>
              <li>Scrape or harvest data from our Service</li>
            </ul>
          </section>

          <section>
            <h2>Content You Submit</h2>
            <p>
              You retain ownership of all content you submit to our Service. By uploading
              content, you grant us a limited license to:
            </p>
            <ul>
              <li>Process and analyze your content as part of our Service</li>
              <li>Store your content temporarily for analysis purposes</li>
              <li>Store analysis results for your future reference</li>
            </ul>
            <p>
              We do not claim ownership of your content and will not use it for purposes
              other than providing our Service to you.
            </p>
          </section>

          <section>
            <h2>Credits and Payment</h2>
            <p>
              Scans are paid for with credits. New accounts receive a one-time grant of free
              credits, and additional credits can be purchased in packs.
            </p>
            <ul>
              <li>
                The number of credits a scan costs depends on the length of the video. The cost
                is shown before you run a scan, and your balance is shown in your dashboard.
              </li>
              <li>
                Credits do not expire.
              </li>
              <li>
                Credits have no cash value and cannot be exchanged for money or transferred
                between accounts.
              </li>
              <li>
                Prices and credit costs may change. Changes will not affect credits you have
                already purchased.
              </li>
            </ul>

            <h3>Refunds</h3>
            <p>
              If a scan fails because of a problem on our side, the credits for that scan are
              automatically returned to your balance. If you believe you were charged for a
              scan that did not complete and the credits were not returned, contact us at
              support@mycleanstudio.com and we will refund them.
            </p>
            <p>
              Otherwise, credit purchases are final. Because credits are delivered to your
              account immediately, we do not offer refunds for unused credits or for scans that
              completed successfully but did not produce the result you hoped for. Our analysis
              is automated and advisory &mdash; see Service Limitations below.
            </p>
            <p>
              If your account is terminated for a breach of these Terms, any remaining credits
              are forfeited. If we discontinue the Service, we will refund unused purchased
              credits.
            </p>
          </section>

          <section>
            <h2>Service Limitations</h2>
            <p>
              <strong>Our Service is a tool to assist content creators, not a guarantee
              of platform compliance.</strong> You acknowledge that:
            </p>
            <ul>
              <li>Platform guidelines change frequently and our analysis may not reflect the latest updates</li>
              <li>Our analysis is automated and may not catch all potential issues</li>
              <li>Final compliance decisions are made by the platforms themselves</li>
              <li>We are not responsible for content removal, demonetization, or account actions taken by platforms</li>
            </ul>
          </section>

          <section>
            <h2>Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY
              KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul>
              <li>MERCHANTABILITY</li>
              <li>FITNESS FOR A PARTICULAR PURPOSE</li>
              <li>NON-INFRINGEMENT</li>
              <li>ACCURACY OR COMPLETENESS OF RESULTS</li>
            </ul>
            <p>
              We do not warrant that the Service will be uninterrupted, error-free, or
              completely secure.
            </p>
          </section>

          <section>
            <h2>Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, MYCLEANSTUDIO SHALL NOT BE LIABLE FOR
              ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING:
            </p>
            <ul>
              <li>Loss of revenue or profits</li>
              <li>Loss of data</li>
              <li>Content demonetization or removal by platforms</li>
              <li>Account suspension or termination by platforms</li>
              <li>Any damages resulting from reliance on our analysis</li>
            </ul>
          </section>

          <section>
            <h2>Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless MyCleanStudio and its affiliates from
              any claims, damages, or expenses arising from:
            </p>
            <ul>
              <li>Your use of the Service</li>
              <li>Content you submit to the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
            </ul>
          </section>

          <section>
            <h2>Intellectual Property</h2>
            <p>
              The Service, including its design, features, and content (excluding user-submitted
              content), is owned by MyCleanStudio and protected by intellectual property laws.
              You may not copy, modify, or distribute any part of our Service without permission.
            </p>
          </section>

          <section>
            <h2>Termination</h2>
            <p>
              We reserve the right to suspend or terminate your access to the Service at any
              time, with or without cause, with or without notice. Upon termination:
            </p>
            <ul>
              <li>Your right to use the Service will immediately cease</li>
              <li>Your stored data may be deleted</li>
              <li>Any remaining credits are forfeited, as described in Credits and Payment</li>
              <li>Provisions that should survive termination will remain in effect</li>
            </ul>
          </section>

          <section>
            <h2>Changes to Terms</h2>
            <p>
              We may modify these Terms at any time. We will notify you of material changes
              by posting the updated Terms on our website. Your continued use of the Service
              after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section>
            <h2>Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of
              the jurisdiction in which MyCleanStudio operates, without regard to conflict
              of law principles.
            </p>
          </section>

          <section>
            <h2>Severability</h2>
            <p>
              If any provision of these Terms is found to be unenforceable, the remaining
              provisions will continue in full force and effect.
            </p>
          </section>

          <section>
            <h2>Contact Us</h2>
            <p>
              If you have questions about these Terms, please contact us at:
            </p>
            <p>
              <a href="mailto:support@mycleanstudio.com">support@mycleanstudio.com</a>
            </p>
          </section>
        </main>

      </div>
        {/* Footer */}
        <MyCleanStudioFooter />
      </div>
    </>
  );
}

export default TermsOfService;
